import {
  CONTRACT_STATUSES,
  CURRENCIES,
  CUSTOMER_STATUSES,
  PAYMENT_PROVIDERS,
  type CustomerMasterReport,
} from '@vy/types';
import { useMemo } from 'react';
import type { FilterField } from '../../../../components/new-design/vyTableFilter';
import {
  buildCountryFilterField,
  buildTextFilterField,
  collectUniqueOptions,
  filterNonEmptySelectFields,
  MULTISELECT_OPERATORS,
} from '../../reports-components/shared-filter-fields';
import { CUSTOMER_MASTER_FILTER_FIELD_IDS } from '../utils/customer-master-filter-field-ids';

const YES_NO_OPTIONS = Object.freeze([
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
]);

export function useCustomerMasterFilterFields(report: CustomerMasterReport): FilterField[] {
  return useMemo(() => {
    const countries = collectUniqueOptions(
      report.map((customer) => customer.address?.country ?? null),
    );
    const subsidiaries = collectUniqueOptions(
      report.map((customer) => customer.subsidiary ?? null),
    );
    const parentCustomers = collectUniqueOptions(
      report.map((customer) => customer.parentCustomer ?? null),
    );
    const customerTypes = collectUniqueOptions(
      report.map((customer) => customer.customerType ?? null),
    );
    const taxMethods = collectUniqueOptions(
      report.map((customer) => customer.taxMethod ?? null),
    );
    const accountManagers = collectUniqueOptions(
      report.flatMap((customer) =>
        customer.contracts.map((contract) => contract.accountManager ?? null),
      ),
    );

    const allFields: FilterField[] = [
      // Customer-level fields
      buildTextFilterField(CUSTOMER_MASTER_FILTER_FIELD_IDS.CustomerName, 'Customer name'),
      {
        id: CUSTOMER_MASTER_FILTER_FIELD_IDS.CustomerStatus,
        label: 'Customer status',
        inputType: 'select',
        operators: MULTISELECT_OPERATORS,
        options: Object.values(CUSTOMER_STATUSES).map((status) => ({ value: status, label: status })),
      },
      buildTextFilterField(CUSTOMER_MASTER_FILTER_FIELD_IDS.CustomerId, 'Customer ID'),
      buildTextFilterField(CUSTOMER_MASTER_FILTER_FIELD_IDS.LegalName, 'Legal name'),
      buildTextFilterField(CUSTOMER_MASTER_FILTER_FIELD_IDS.CustomerAlias, 'Customer alias'),
      buildTextFilterField(CUSTOMER_MASTER_FILTER_FIELD_IDS.DueDate, 'Due date'),
      {
        id: CUSTOMER_MASTER_FILTER_FIELD_IDS.ParentCustomer,
        label: 'Parent customer',
        inputType: 'select',
        operators: MULTISELECT_OPERATORS,
        options: parentCustomers,
      },
      buildTextFilterField(CUSTOMER_MASTER_FILTER_FIELD_IDS.CrmId, 'CRM ID'),
      buildTextFilterField(CUSTOMER_MASTER_FILTER_FIELD_IDS.NsErpId, 'NS Customer ERP ID'),
      buildTextFilterField(CUSTOMER_MASTER_FILTER_FIELD_IDS.QbErpId, 'QB Customer ERP ID'),
      buildTextFilterField(CUSTOMER_MASTER_FILTER_FIELD_IDS.XeroErpId, 'Xero Customer ERP ID'),
      buildTextFilterField(CUSTOMER_MASTER_FILTER_FIELD_IDS.StripeId, 'Stripe ID'),
      {
        id: CUSTOMER_MASTER_FILTER_FIELD_IDS.Subsidiary,
        label: 'Subsidiary',
        inputType: 'select',
        operators: MULTISELECT_OPERATORS,
        options: subsidiaries,
      },
      buildCountryFilterField(countries),
      {
        id: CUSTOMER_MASTER_FILTER_FIELD_IDS.CustomerType,
        label: 'Customer type',
        inputType: 'select',
        operators: MULTISELECT_OPERATORS,
        options: customerTypes,
      },
      {
        id: CUSTOMER_MASTER_FILTER_FIELD_IDS.CustomerCurrency,
        label: 'Currency',
        inputType: 'select',
        operators: MULTISELECT_OPERATORS,
        options: Object.values(CURRENCIES).map((currency) => ({ value: currency, label: currency })),
      },
      {
        id: CUSTOMER_MASTER_FILTER_FIELD_IDS.TaxMethod,
        label: 'Tax method',
        inputType: 'select',
        operators: MULTISELECT_OPERATORS,
        options: taxMethods,
      },
      {
        id: CUSTOMER_MASTER_FILTER_FIELD_IDS.ManualReview,
        label: 'Manual review',
        inputType: 'select',
        operators: MULTISELECT_OPERATORS,
        options: [...YES_NO_OPTIONS],
      },
      {
        id: CUSTOMER_MASTER_FILTER_FIELD_IDS.PaymentProvider,
        label: 'Primary payment provider',
        inputType: 'select',
        operators: MULTISELECT_OPERATORS,
        options: Object.values(PAYMENT_PROVIDERS).map((provider) => ({
          value: provider,
          label: provider,
        })),
      },
      {
        id: CUSTOMER_MASTER_FILTER_FIELD_IDS.AutoPay,
        label: 'Auto pay',
        inputType: 'select',
        operators: MULTISELECT_OPERATORS,
        options: [...YES_NO_OPTIONS],
      },

      // Contract-level fields
      buildTextFilterField(CUSTOMER_MASTER_FILTER_FIELD_IDS.ContractName, 'Contract name'),
      {
        id: CUSTOMER_MASTER_FILTER_FIELD_IDS.ContractStatus,
        label: 'Contract status',
        inputType: 'select',
        operators: MULTISELECT_OPERATORS,
        options: Object.values(CONTRACT_STATUSES).map((status) => ({ value: status, label: status })),
      },
      buildTextFilterField(CUSTOMER_MASTER_FILTER_FIELD_IDS.ContractId, 'Contract ID'),
      {
        id: CUSTOMER_MASTER_FILTER_FIELD_IDS.Currency,
        label: 'Contract currency',
        inputType: 'select',
        operators: MULTISELECT_OPERATORS,
        options: Object.values(CURRENCIES).map((currency) => ({ value: currency, label: currency })),
      },
      {
        id: CUSTOMER_MASTER_FILTER_FIELD_IDS.AutoRenewal,
        label: 'Auto renewal',
        inputType: 'select',
        operators: MULTISELECT_OPERATORS,
        options: [...YES_NO_OPTIONS],
      },
      buildTextFilterField(CUSTOMER_MASTER_FILTER_FIELD_IDS.PurchaseOrder, 'Purchase order'),
      {
        id: CUSTOMER_MASTER_FILTER_FIELD_IDS.AccountManager,
        label: 'AE',
        inputType: 'select',
        operators: MULTISELECT_OPERATORS,
        options: accountManagers,
      },
      buildTextFilterField(
        CUSTOMER_MASTER_FILTER_FIELD_IDS.SalesforceOpportunityId,
        'Salesforce Opportunity ID',
      ),
      buildTextFilterField(
        CUSTOMER_MASTER_FILTER_FIELD_IDS.NetSuiteSalesOrderId,
        'NetSuite Sales Order ID',
      ),
    ];

    return filterNonEmptySelectFields(allFields);
  }, [report]);
}
